<template>
  <default-modal
    v-model="opened"
    name="invoice-send-modal"
    :actionLabel="$t('send')"
    :autofocus="$refs.message"
    :successHandler="successHandler"
    :title="$t('invoice_send_modal_title')"
    width="md"
    @show="handleShow"
  >
    <template slot="content" v-if="!success">
      <q-field
        v-if="addresses.length"
        :error="errors.has('email_address')"
        :error-label="errors.first('email_address')"
        class="q-ma-none"
      >
        <q-input
          v-model="formData.address"
          type="email"
          name="emailAddress"
          :float-label="$t('email_to')"
          hide-underline
        >
          <q-autocomplete :static-data="{ field: 'value', list: addresses }" :min-characters="0" />
        </q-input>
      </q-field>

      <q-field
        v-else
        :error="errors.has('email_address')"
        :error-label="errors.first('email_address')"
        class="q-ma-none"
      >
        <q-input
          v-model="formData.address"
          type="email"
          name="emailAddress"
          :float-label="$t('email_to')"
        />
      </q-field>

      <vue-simplemde
        ref="markdown"
        v-model="formData.message"
        :configs="simpleMdeConfig"
        class="q-mt-lg"
      />
    </template>

    <template slot="content" v-else>
      <check-mark></check-mark>
      <h4 class="text-center">{{ $t('send_modal_successful_title') }}</h4>
      <p class="text-center">{{ $t('invoice_send_modal_successful_body', { address: formData.address }) }}</p>
    </template>
  </default-modal>
</template>

<script>
  import 'simplemde/dist/simplemde.min.css'
  import CheckMark from 'components/Common/Animations/Checkmark.vue'
  import { CompanyClient } from 'src/api/Companies'
  import DefaultModal from 'components/Modal/DefaultModal.vue'
  import { InvoiceClient } from 'src/api/Invoices'
  import { PersonClient } from 'src/api/Persons'
  import SimpleMDE from 'simplemde'
  import VueSimplemde from 'vue-simplemde'
  import { createErrorFromResponse } from 'src/utils/error'
  import { formatMoney } from 'src/utils/format'
  import { mapGetters } from 'vuex'

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        invoice: null,
        addresses: [],
        opened: false,
        loading: false,
        success: false,
        formData: {
          address: '',
          message: ''
        }
      }
    },

    computed: {
      ...mapGetters(['activeTeam']),
      successHandler () {
        return !this.success ? this.handleSend : null
      },
      simpleMdeConfig () {
        return {
          autoDownloadFontAwesome: false,
          spellChecker: false,
          toolbar: [
            {
              name: 'bold',
              action: SimpleMDE.toggleBold,
              className: 'material-icons mdi-bold',
              title: 'Bold'
            },
            {
              name: 'italic',
              action: SimpleMDE.toggleItalic,
              className: 'material-icons mdi-italic',
              title: 'Italic'
            },
            {
              name: 'heading',
              action: SimpleMDE.toggleHeadingSmaller,
              className: 'material-icons mdi-header',
              title: 'Heading'
            },
            '|',
            {
              name: 'unordered-list',
              action: SimpleMDE.toggleUnorderedList,
              className: 'material-icons mdi-list',
              title: 'Generic List'
            },
            {
              name: 'ordered-list',
              action: SimpleMDE.toggleOrderedList,
              className: 'material-icons mdi-list-ol',
              title: 'Numbered List'
            },
            '|',
            {
              name: 'clean-block',
              action: SimpleMDE.cleanBlock,
              className: 'material-icons mdi-clear',
              title: 'Clean block'
            },
            {
              name: 'preview',
              action: SimpleMDE.togglePreview,
              className: 'material-icons mdi-eye no-disable',
              title: 'Toggle Preview'
            }
          ],
          status: false
        }
      }
    },

    methods: {
      open (invoice = null) {
        this.invoice = invoice
        this.opened = true
        this.loading = false
        this.success = false

        // In the invoice table the total_gross attribute is already formatted
        const invoiceTotal = typeof invoice.total_gross === 'string' ? invoice.total_gross : formatMoney(invoice.total_gross, invoice.currency)

        this.formData.message = this.$t('invoice_mail_default', { invoiceTotal, teamName: this.activeTeam.name })
        this.$refs.markdown.simplemde.codemirror.refresh()
      },

      handleShow () {
        // Modal is not destroyed on close so when opened again the value change is not detected.
        this.$refs.markdown.simplemde.value(this.formData.message)
        this.$refs.markdownmodelVal = this.formData.message
        this.$refs.markdown.simplemde.codemirror.refresh()
      },

      handleSend () {
        const data = {
          'email_address': this.formData.address,
          'body': this.formData.message
        }

        this.$validator.validateAll(data)
          .then(result => {
            if (!result) {
              return
            }

            return new InvoiceClient()
              .send(this.invoice.id, data)
              .then(() => {
                this.success = true

                this.$emit('sent', this.invoice)
              })
              .catch(error => createErrorFromResponse(error))
          })
      },

      reset () {
        this.invoice = null
        this.addresses = []
        this.opened = false
        this.loading = false
        this.success = false
        this.formData = {
          address: '',
          message: ''
        }
      }
    },

    watch: {
      invoice (val) {
        if (!val) {
          return
        }

        const customer = val.customer.data
        const client = customer.type === 'person' ? new PersonClient() : new CompanyClient()
        client.include(['email_addresses'])
          .get(customer.id)
          .then(response => {
            const customer = response.data.data[0]

            if (customer.hasOwnProperty('email_addresses')) {
              this.addresses = customer.email_addresses.data.map(address => ({
                icon: customer.default_email_address_id === address.id ? 'star' : null,
                label: address.email,
                sublabel: address.label,
                value: address.email
              }))

              if (customer.hasOwnProperty('default_email_address_id')) {
                this.formData.address = customer.email_addresses.data.find(address => {
                  return address.id === customer.default_email_address_id
                }).email
              }
            }
          })
      },

      opened (val) {
        if (!val) {
          this.reset()
        }
      }
    },

    created () {
      this.$validator.attach({ name: 'email_address', rules: 'required|email', alias: this.$t('email') })
      this.$validator.attach({ name: 'body', rules: 'required|max:15000', alias: this.$t('message') })
    },

    components: {
      CheckMark,
      DefaultModal,
      VueSimplemde
    }
  }
</script>

<style lang="stylus">
  .editor-toolbar
    .material-icons
      line-height 32px !important;
    .mdi-bold:after
      content "format_bold"
    .mdi-italic:after
      content "format_italic"
    .mdi-header:after
      content "title"
    .mdi-list:after
      content "format_list_bulleted"
    .mdi-list-ol:after
      content "format_list_numbered"
    .mdi-eye:after
      content "remove_red_eye"
    .mdi-clear:after
      content "format_clear"
</style>
